import FeatureGates from '@atlaskit/feature-gate-js-client';
import { UFOv2 } from '@atlassian/post-office-frontend-performance-tracking';
import { MessageContextProvider, type MessageContextData } from '@post-office/message-context';
import { RecommendationProvider } from '@post-office/recommendation-context';
import type { MessageCreationType } from '@post-office/shared-contracts/message-template';
import {
	type FunctionComponent,
	type ReactNode,
	Suspense,
	type SuspenseProps,
	useMemo,
} from 'react';
import { MaybeI18nProvider } from '../i18n/i18nProvider';
import { ChoreographerWrapper } from './choreographer-wrapper';
import { MaybeChoreographerWrapper } from './maybe-choreographer-wrapper';
import { FeatureFlaggedUfoWrapper } from './ufo-wrapper';
import { TriggeredMessageValidator } from './triggered-message-validator';
import { MaybeTriggeredMessageValidator } from './maybe-triggered-message-validator';
import { MessageAnalyticsContextProvider } from '../message-analytics-context';
import { MaybeManualMessageViewedEvent } from '@post-office/placement-contexts/maybe-manual-message-viewed-event';
import { type MessageCategory } from '../types';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { type JSX } from 'react/jsx-runtime';
import { useSubscriptionConfig } from '../create-placement-component/providers/stable-graphql-subscription';

type ComponentProps = {
	transactionAccountId?: string;
};

type ProviderProps = {
	messageTemplateId: string;
	messageInstanceId: string;
	messageCategory: MessageCategory;
	transactionAccountId?: string;
	recommendationSession?: {
		sessionId: string;
		entityid: string;
	};
	product?: string;
	loadingFallback?: React.ReactNode;
	postOffice?: {
		analyticsDetails?: MessageContextData['analyticsDetails'];
	};
	triggerId?: string;
	messageCreationType?: MessageCreationType;
	messageSuccessComponent?: JSX.Element;
	componentProps: ComponentProps;
	isUFOEnabled: boolean;
	children?: ReactNode;
	SuspenseBoundary?: FunctionComponent<SuspenseProps>;
	waitForTrigger?: WaitForTriggerFunction;
	configSetId?: string;
	journeyId?: string;
	journeyTriggerId?: string;
	placementId?: string;
	isMaybeI18nProviderEnabledCallback?: () => boolean;
};

const DEFAULT_RECOMMENDATION_SESSION = {};

export const MessageRendererProvider = ({
	messageInstanceId,
	messageTemplateId,
	messageCategory,
	recommendationSession,
	messageSuccessComponent,
	componentProps,
	loadingFallback,
	triggerId,
	messageCreationType,
	postOffice,
	isUFOEnabled,
	children,
	SuspenseBoundary = Suspense,
	waitForTrigger,
	configSetId,
	journeyId,
	journeyTriggerId,
	placementId,
	isMaybeI18nProviderEnabledCallback,
}: ProviderProps): JSX.Element => {
	const subscriptionConfig = useSubscriptionConfig();
	const subscriptionEnabled = subscriptionConfig.subscription?.enabled ?? false;

	/**
	 * Type guard: messageContextMemo must match MessageContextData exactly.
	 * If a field is missing or wrong type, the type annotation will catch it.
	 */
	const loadingComponent = useMemo(
		() =>
			isUFOEnabled ? (
				<UFOv2.UFOLoadHold name={UFOv2.HoldNames.MESSAGE}>
					{loadingFallback ?? null}
				</UFOv2.UFOLoadHold>
			) : (
				<>{loadingFallback ?? null}</>
			),
		[loadingFallback, isUFOEnabled],
	);

	const messageContextMemo = useMemo<MessageContextData>(
		() => ({
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			transactionAccountId: componentProps.transactionAccountId,
			analyticsDetails: {
				...postOffice?.analyticsDetails,
				subscriptionEnabled,
			},
			journeyId,
			journeyTriggerId,
			configSetId,
		}),
		[
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			componentProps.transactionAccountId,
			postOffice?.analyticsDetails,
			subscriptionEnabled,
			journeyId,
			journeyTriggerId,
			configSetId,
		],
	);

	const isMaybeI18nEnabled = isMaybeI18nProviderEnabledCallback?.() ?? false;

	// TODO: clean up the fallback branching logic and remove this gate once
	// 'post-office_enable-triggered-validator-fix' rolls out to 100%.
	// Switcheroo: https://switcheroo.atlassian.com/ui/gates/key/post-office_enable-triggered-validator-fix
	if (FeatureGates.checkGate('post-office_enable-triggered-validator-fix')) {
		// NEW TREE (gate ON).
		// - MaybeChoreographerWrapper sits at the top so it can claim its slot even
		//   while the lazy template is still loading; it no-ops for client-triggered
		//   messages, which handle their own choreography via
		//   MaybeTriggeredMessageValidator below.
		// - MaybeTriggeredMessageValidator must sit BELOW SuspenseBoundary so it only
		//   mounts after the lazy template chunk has resolved. Otherwise the validator
		//   commits with the Suspense fallback as its children (template hasn't
		//   executed yet, ref is false); when the chunk later resolves only the
		//   Suspense subtree re-renders, so the validator above never sees the ref
		//   flip to true and stays stuck in 'pending' with `display:none`.
		return (
			<MaybeChoreographerWrapper
				ChoreographerWrapper={ChoreographerWrapper}
				enabled={!waitForTrigger}
				messageId={messageInstanceId}
				messageTemplateId={messageTemplateId}
				messageCategory={messageCategory}
			>
				<FeatureFlaggedUfoWrapper messageTemplateId={messageTemplateId} isEnabled={isUFOEnabled}>
					<MessageContextProvider value={messageContextMemo}>
						<RecommendationProvider value={recommendationSession ?? DEFAULT_RECOMMENDATION_SESSION}>
							<SuspenseBoundary fallback={loadingComponent}>
								<MaybeTriggeredMessageValidator waitForTrigger={waitForTrigger}>
									<MessageAnalyticsContextProvider>
										<MaybeManualMessageViewedEvent isManual={!!waitForTrigger}>
											<MaybeI18nProvider
												messageTemplateId={messageTemplateId}
												placementId={placementId}
												isI18nMigrationInAppEnabled={isMaybeI18nEnabled}
											>
												{!waitForTrigger && messageSuccessComponent}
												{children}
											</MaybeI18nProvider>
										</MaybeManualMessageViewedEvent>
									</MessageAnalyticsContextProvider>
								</MaybeTriggeredMessageValidator>
							</SuspenseBoundary>
						</RecommendationProvider>
					</MessageContextProvider>
				</FeatureFlaggedUfoWrapper>
			</MaybeChoreographerWrapper>
		);
	}

	// FALLBACK TREE (gate OFF — pre-fix behaviour). Kept verbatim from before the
	// triggered-validator-fix PR so a kill-switch flip restores the exact previous
	// runtime structure. Known issue: when the template is lazy-loaded under
	// SuspenseBoundary, TriggeredMessageValidator mounts ABOVE Suspense, commits
	// before the template has run, and never re-runs its useLayoutEffect when the
	// chunk resolves — leaving client-triggered messages stuck behind
	// `display:none`. This is what the new tree above fixes.
	const messageContent = (
		<FeatureFlaggedUfoWrapper messageTemplateId={messageTemplateId} isEnabled={isUFOEnabled}>
			<MessageContextProvider value={messageContextMemo}>
				<RecommendationProvider value={recommendationSession ?? DEFAULT_RECOMMENDATION_SESSION}>
					<SuspenseBoundary fallback={loadingComponent}>
						<MessageAnalyticsContextProvider>
							<MaybeManualMessageViewedEvent isManual={!!waitForTrigger}>
								<MaybeI18nProvider
									messageTemplateId={messageTemplateId}
									placementId={placementId}
									isI18nMigrationInAppEnabled={isMaybeI18nEnabled}
								>
									{!waitForTrigger && messageSuccessComponent}
									{children}
								</MaybeI18nProvider>
							</MaybeManualMessageViewedEvent>
						</MessageAnalyticsContextProvider>
					</SuspenseBoundary>
				</RecommendationProvider>
			</MessageContextProvider>
		</FeatureFlaggedUfoWrapper>
	);

	if (waitForTrigger) {
		return (
			<TriggeredMessageValidator waitForTrigger={waitForTrigger}>
				{messageContent}
			</TriggeredMessageValidator>
		);
	}

	return (
		<ChoreographerWrapper
			messageId={messageInstanceId}
			messageTemplateId={messageTemplateId}
			messageCategory={messageCategory}
		>
			{messageContent}
		</ChoreographerWrapper>
	);
};
